import { Calendar, Clock3, FileText, Lock, MapPin, Users } from 'lucide-react'
import type { OpenPermitListItem } from 'modules/permit/api/permitService'
import { getPermitStatusBadgeClass, getPermitStatusLabel } from 'modules/permit/constants/permitStatus'
import { Badge } from 'shared/ui/badge'
import { Button } from 'shared/ui/button'
import { Card, CardContent } from 'shared/ui/card'
import { Checkbox } from 'shared/ui/checkbox'
import { formatDate, formatDateRange, formatSupervisorSummary, isOpenedPermitStatus, resolvePermitTypeBadge } from './utils'

interface ClosePermitCardProps {
  permit: OpenPermitListItem
  selected: boolean
  onToggleSelected: (checked: boolean) => void
  onView: () => void
  onClose: () => void
}

export function ClosePermitCard({ permit, selected, onToggleSelected, onView, onClose }: ClosePermitCardProps) {
  const permitTypeBadge = resolvePermitTypeBadge(permit.permitTypeCode, permit.permitType)
  const canClose = isOpenedPermitStatus(permit.status)
  const supervisorSummary = formatSupervisorSummary(permit)
  const statusLabel = getPermitStatusLabel(permit.status, permit.statusName)
  const statusBadgeClass = getPermitStatusBadgeClass(permit.status)

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="space-y-4 p-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex gap-4">
            <Checkbox checked={selected} disabled={!canClose} onCheckedChange={(checked) => onToggleSelected(checked === true)} />
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <Badge className={`border ${permitTypeBadge.className}`}>{permitTypeBadge.label}</Badge>
                <Badge className={statusBadgeClass}>{statusLabel}</Badge>
                <span className="font-medium text-slate-900">Permit No. : {permit.permitNumber || permit.permitId}</span>
                <span className="inline-flex items-center gap-2 text-slate-500">
                  <Calendar className="h-4 w-4" />
                  วันที่เข้าปฏิบัติงาน : {formatDateRange(permit.plannedStart, permit.plannedEnd)}
                </span>
              </div>

              <p className="text-base font-medium text-slate-900">{permit.workDescription || permit.title || '-'}</p>

              <div className="mt-2 flex flex-wrap gap-2">
                {permit.priority ? <Badge variant="outline">{permit.priority}</Badge> : null}
                {(permit.selectedSubActivities ?? []).map((subactivity) => (
                  <Badge key={`${permit.permitId}-${subactivity.subactivityId}`} variant="outline" className="border-slate-200 text-slate-700">
                    {subactivity.name || subactivity.code}
                  </Badge>
                ))}
              </div>

              <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                <span className="inline-flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  {permit.location || '-'}
                </span>
                <span className="inline-flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  จำนวนผู้เข้าปฏิบัติงาน : {permit.workersCount ?? 0} คน
                </span>
              </div>

              <div className="text-sm text-slate-600">
                <span className="font-medium text-slate-900">ผู้ขอ Permit :</span> {permit.requesterName || '-'} ({permit.requesterCompany || '-'})
                {permit.areaOwner ? <span> | ผู้ควบคุมงาน : {permit.areaOwner}</span> : null}
              </div>

              {permit.openedAt || permit.openedByName ? (
                <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
                  <p className="inline-flex items-center gap-2 font-medium text-sky-800">
                    <Clock3 className="h-4 w-4" />
                    เปิดโดย: {permit.openedByName || '-'}
                    {permit.openedAt ? <span>({formatDate(permit.openedAt, true)})</span> : null}
                  </p>
                </div>
              ) : null}

              {permit.closedAt || permit.closedByName ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900">
                  <p className="inline-flex items-center gap-2 font-medium text-rose-800">
                    <Lock className="h-4 w-4" />
                    ปิดโดย: {permit.closedByName || '-'}
                    {permit.closedAt ? <span>({formatDate(permit.closedAt, true)})</span> : null}
                  </p>
                </div>
              ) : null}

              <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                <p className="text-xs uppercase text-emerald-700">พนักงานควบคุมดูแล</p>
                <p className="mt-1">{supervisorSummary}</p>
              </div>
            </div>
          </div>

          <div className="flex min-w-[180px] flex-col gap-2">
            <Button variant="outline" onClick={onView}>
              <FileText className="mr-2 h-4 w-4" />
              ดูรายละเอียด
            </Button>
            <Button
              className="bg-rose-500 text-white hover:bg-rose-600"
              disabled={!canClose}
              onClick={onClose}
            >
              <Lock className="mr-2 h-4 w-4" />
              ปิด Permit
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
